import { useEffect, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { connect } from "react-redux";

import { useLoading } from "app/custom-hooks/loadingHook";
import { SegmentsSingleMarketingChannelsGraphChart } from "app/pages/segments/mmx/SegmentsSingleMarketingGraphChart";
import type { IBaseSingleRequestParams, ICustomSegmentAccount } from "app/services/segments/segmentsApiService";
import SegmentsApiService from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { transformGraphMMXDataToBarChartData } from "./mmxGraphData";
import type { ISegmentsParameters } from "./SegmentsMmxSingleOverview";
import { SegmentsMmxSingleOverview } from "./SegmentsMmxSingleOverview";
import { MarketingChannelsBoxContainer } from "./styledComponents";

interface ISegmentsSingleMarketingChannelsContainer {
    organizationSegments: ICustomSegmentAccount[];
}

const SegmentsSingleMarketingChannelsContainer: React.FC<ISegmentsSingleMarketingChannelsContainer> = (props) => {
    const { organizationSegments } = props;
    const { params }: { params: ISegmentsParameters } = useCurrentStateAndParams();
    const apiParams = useMemo(() => proNavigator.getApiParams(params), [params]);
    const { id, webSource } = params;

    const segmentData = useMemo(() => {
        return SegmentsUtils.getSegmentById({ segments: organizationSegments }, id) as ICustomSegmentAccount;
    }, [organizationSegments, id]);

    const { segmentsApiService } = useMemo(
        () => ({
            segmentsApiService: new SegmentsApiService(),
        }),
        [],
    );
    const [segmentMmxGraphChartData, segmentMmxGraphDataOps] = useLoading();

    // This is the BarChart Data call, but because of performance issues we transform the Data retuning from Graph call to fit bar chart
    // const isMmxBarChartLoading = [useLoading.STATES.INIT, useLoading.STATES.LOADING].includes(
    //     segmentMmxBarChartData.state,
    // );

    const isGraphChartDataLoading = [useLoading.STATES.INIT, useLoading.STATES.LOADING].includes(
        segmentMmxGraphChartData.state,
    );

    useEffect(() => {
        if (!segmentData || segmentData.isLocked) {
            if (segmentData?.isLocked) {
                proNavigator.go("digitalsuite_segments-homepage");
            }
        }
    }, [segmentData]);

    useEffect(() => {
        segmentData &&
            segmentMmxGraphDataOps.load(() =>
                segmentsApiService.getCustomSegmentMarketingMixGraphData({
                    ...apiParams,
                    id: undefined,
                    segmentsIds: id,
                    keys: "null",
                    webSource,
                    includeSubDomains: true,
                    lastUpdated: segmentData.lastUpdated,
                } as IBaseSingleRequestParams),
            );
    }, [segmentData?.lastUpdated, segmentsApiService, apiParams, id, webSource]);

    const barChartData = useMemo(() => {
        return transformGraphMMXDataToBarChartData(segmentMmxGraphChartData.data, id);
    }, [segmentMmxGraphChartData.data, id]);

    return (
        <MarketingChannelsBoxContainer data-automation="SegmentsSingleMarketingChannelsContainer">
            <SegmentsMmxSingleOverview
                params={params}
                data={barChartData}
                isLoading={isGraphChartDataLoading}
            />
            <SegmentsSingleMarketingChannelsGraphChart
                params={params}
                data={segmentMmxGraphChartData.data}
                isLoading={isGraphChartDataLoading}
                segmentData={segmentData}
            />
        </MarketingChannelsBoxContainer>
    );
};

function mapStateToProps(store: {
    segmentsModule: { customSegmentsMeta: { AccountSegments: ICustomSegmentAccount[] } };
}) {
    const {
        segmentsModule: { customSegmentsMeta },
    } = store;
    return {
        organizationSegments: customSegmentsMeta?.AccountSegments,
    };
}

export default connect(mapStateToProps, undefined)(SegmentsSingleMarketingChannelsContainer);
